import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { cn } from '../utils/cn';
import { homepageContentApi } from '../services/api';

const defaultCompanyInfo = {
  name_zh: '惠亚建筑工程有限公司', name_vi: 'Công ty TNHH Xây dựng Hueya',
  founded_zh: '2015年', founded_vi: '2015',
  location_zh: '越南 北宁省 北宁市', location_vi: 'Bắc Ninh, tỉnh Bắc Ninh, Việt Nam',
  mission_zh: '为工业厂房提供专业、高效、安全的建筑工程服务，助力客户在越南市场成功落地。',
  mission_vi: 'Cung cấp dịch vụ xây dựng công trình chuyên nghiệp, hiệu quả và an toàn cho nhà xưởng công nghiệp, hỗ trợ khách hàng triển khai thành công trên thị trường Việt Nam.',
  vision_zh: '成为越南领先的工业厂房建筑工程服务商，打造中越建筑合作的标杆企业。',
  vision_vi: 'Trở thành nhà cung cấp dịch vụ xây dựng nhà xưởng công nghiệp hàng đầu tại Việt Nam, xây dựng doanh nghiệp tiêu biểu trong hợp tác xây dựng Trung-Việt.'
};

const defaultMilestones = [
  { year: '2015', title_zh: '公司成立', title_vi: 'Thành lập công ty', desc_zh: '在北宁省成立，专注工业厂房建筑工程', desc_vi: 'Thành lập tại tỉnh Bắc Ninh, tập trung vào xây dựng nhà xưởng công nghiệp' },
  { year: '2017', title_zh: '首家中资工厂项目', title_vi: 'Dự án nhà máy Trung Quốc đầu tiên', desc_zh: '完成首个中资企业工业厂房项目，获得客户高度认可', desc_vi: 'Hoàn thành dự án nhà xưởng công nghiệp đầu tiên cho doanh nghiệp Trung Quốc, nhận được đánh giá cao từ khách hàng' },
  { year: '2019', title_zh: '业务拓展', title_vi: 'Mở rộng kinh doanh', desc_zh: '新增电气、暖通空调、通风系统等专业服务', desc_vi: 'Bổ sung các dịch vụ chuyên môn như điện, HVAC, hệ thống thông gió' },
  { year: '2022', title_zh: '规模扩张', title_vi: 'Mở rộng quy mô', desc_zh: '完成50+工业厂房项目，服务多家外资企业', desc_vi: 'Hoàn thành 50+ dự án nhà xưởng công nghiệp, phục vụ nhiều doanh nghiệp FDI' }
];

const defaultValues = [
  { icon: '🔧', title_zh: '专业品质', title_vi: 'Chất lượng chuyên nghiệp', desc_zh: '严格把控工程质量，确保每个项目达到行业最高标准', desc_vi: 'Kiểm soát chặt chẽ chất lượng công trình, đảm bảo mỗi dự án đạt tiêu chuẩn cao nhất của ngành' },
  { icon: '🤝', title_zh: '诚信合作', title_vi: 'Hợp tác chân thành', desc_zh: '以诚待人，建立长期稳定的合作伙伴关系', desc_vi: 'Chân thành với mọi người, xây dựng quan hệ đối tác ổn định lâu dài' },
  { icon: '💡', title_zh: '创新求实', title_vi: 'Đổi mới và thực tế', desc_zh: '不断优化施工工艺，提升工程效率与质量', desc_vi: 'Liên tục tối ưu hóa quy trình thi công, nâng cao hiệu quả và chất lượng công trình' },
  { icon: '🌱', title_zh: '持续发展', title_vi: 'Phát triển bền vững', desc_zh: '关注环保与可持续发展，建设绿色工程', desc_vi: 'Quan tâm đến môi trường và phát triển bền vững, xây dựng công trình xanh' }
];

const defaultTeamStats = [
  { label_zh: '专业工程师', label_vi: 'Kỹ sư chuyên nghiệp', value: '50+' },
  { label_zh: '项目经理', label_vi: 'Quản lý dự án', value: '10+' },
  { label_zh: '中越双语团队', label_vi: 'Đội ngũ song ngữ Trung-Việt', value: '100%' }
];

const adaptLang = (lang: string) => (lang === 'vi' ? 'vi' : 'zh') as 'zh' | 'vi';

const AboutPage: React.FC = () => {
  const { language, t } = useLanguage();
  const lang = adaptLang(language);

  const [companyInfo, setCompanyInfo] = useState(defaultCompanyInfo);
  const [milestones, setMilestones] = useState(defaultMilestones);
  const [values, setValues] = useState(defaultValues);
  const [teamStats, setTeamStats] = useState(defaultTeamStats);

  useEffect(() => {
    Promise.all([
      homepageContentApi.getBySection('about_company'),
      homepageContentApi.getBySection('about_milestones'),
      homepageContentApi.getBySection('about_values'),
      homepageContentApi.getBySection('about_team'),
    ]).then(([cRes, mRes, vRes, tRes]) => {
      if (cRes.success && cRes.data?.[0]?.content_zh) {
        try { setCompanyInfo(JSON.parse(cRes.data[0].content_zh)); } catch {}
      }
      if (mRes.success && mRes.data?.[0]?.content_zh) {
        try { setMilestones(JSON.parse(mRes.data[0].content_zh)); } catch {}
      }
      if (vRes.success && vRes.data?.[0]?.content_zh) {
        try { setValues(JSON.parse(vRes.data[0].content_zh)); } catch {}
      }
      if (tRes.success && tRes.data?.[0]?.content_zh) {
        try { setTeamStats(JSON.parse(tRes.data[0].content_zh)); } catch {}
      }
    }).catch(() => {});
  }, []);

  const aboutDesc = [
    `惠亚建筑工程有限公司于${companyInfo.founded_zh}在${companyInfo.location_zh}成立，是一家专注于工业厂房机电工程的综合性建筑服务企业。公司凭借对本地市场的深入了解和专业的技术团队，为中资企业在越南北宁及周边地区的工厂建设提供高品质的建筑工程服务。`,
    `我们致力于为客户提供从设计、施工到维护的全流程工程解决方案。公司始终坚持「质量第一、客户至上」的经营理念，严格把控每一个工程环节，确保项目按时、按质、按预算完成。`,
    `经过多年的发展，惠亚建筑已成功服务于多家知名外资企业，积累了丰富的项目管理经验和良好的市场口碑。我们不仅是工程承包商，更是客户值得信赖的长期合作伙伴。`
  ];

  const aboutDescVI = [
    `Công ty TNHH Xây dựng Hueya được thành lập tại ${companyInfo.location_vi} vào năm ${companyInfo.founded_vi}, là doanh nghiệp dịch vụ xây dựng tổng hợp chuyên về kỹ thuật cơ điện nhà xưởng công nghiệp.`,
    `Chúng tôi cam kết cung cấp giải pháp công trình trọn gói từ thiết kế, thi công đến bảo trì.`,
    `Sau nhiều năm phát triển, Hueya Construction đã phục vụ thành công nhiều doanh nghiệp FDI nổi tiếng, tích lũy kinh nghiệm quản lý dự án phong phú.`
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{language === 'zh' ? '关于我们' : 'Về chúng tôi'}</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              {language === 'zh'
                ? '了解惠亚建筑工程有限公司的专业实力与企业文化'
                : 'Tìm hiểu năng lực chuyên môn và văn hóa doanh nghiệp của Công ty TNHH Xây dựng Hueya'}
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">{language === 'zh' ? '公司概况' : 'Tổng quan công ty'}</h2>
            <div className="space-y-6">
              {(language === 'zh' ? aboutDesc : aboutDescVI).map((p, i) => (
                <p key={i} className="text-lg text-gray-700 leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="font-semibold text-gray-900 mb-2">{language === 'zh' ? '公司名称' : 'Tên công ty'}</h3>
                <p className="text-blue-600">{language === 'zh' ? companyInfo.name_zh : companyInfo.name_vi}</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="font-semibold text-gray-900 mb-2">{language === 'zh' ? '成立时间' : 'Ngày thành lập'}</h3>
                <p className="text-blue-600">{language === 'zh' ? companyInfo.founded_zh : companyInfo.founded_vi}</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="font-semibold text-gray-900 mb-2">{language === 'zh' ? '总部地址' : 'Trụ sở chính'}</h3>
                <p className="text-blue-600">{language === 'zh' ? companyInfo.location_zh : companyInfo.location_vi}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-blue-800 mb-3">{language === 'zh' ? '使命' : 'Sứ mệnh'}</h3>
                <p className="text-gray-700">{language === 'zh' ? companyInfo.mission_zh : companyInfo.mission_vi}</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-blue-800 mb-3">{language === 'zh' ? '愿景' : 'Tầm nhìn'}</h3>
                <p className="text-gray-700">{language === 'zh' ? companyInfo.vision_zh : companyInfo.vision_vi}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">{language === 'zh' ? '发展历程' : 'Hành trình phát triển'}</h2>
          <div className="max-w-3xl mx-auto relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-blue-200"></div>
            {milestones.map((m, i) => (
              <div key={i} className={cn("relative flex items-center mb-12", i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse")}>
                <div className={cn("w-1/2", i % 2 === 0 ? "pr-8 text-right" : "pl-8")}>
                  <span className="text-blue-600 font-bold text-xl">{m.year}</span>
                  <h3 className="text-lg font-semibold text-gray-900 mt-1">{lang === 'zh' ? m.title_zh : m.title_vi}</h3>
                  <p className="text-gray-600 text-sm mt-1">{lang === 'zh' ? m.desc_zh : m.desc_vi}</p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow z-10"></div>
                <div className="w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">{language === 'zh' ? '核心价值观' : 'Giá trị cốt lõi'}</h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            {language === 'zh' ? '这些核心价值观指引着我们的每一步行动和决策' : 'Những giá trị cốt lõi này hướng dẫn mọi hành động và quyết định của chúng tôi'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-lg border text-center">
                <div className="text-5xl mb-4">{v.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{lang === 'zh' ? v.title_zh : v.title_vi}</h3>
                <p className="text-gray-600">{lang === 'zh' ? v.desc_zh : v.desc_vi}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">{language === 'zh' ? '专业团队' : 'Đội ngũ chuyên nghiệp'}</h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            {language === 'zh'
              ? '惠亚建筑拥有一支经验丰富、技术过硬的专业团队，为客户提供优质的工程服务'
              : 'Hueya Construction sở hữu đội ngũ chuyên nghiệp giàu kinh nghiệm và kỹ thuật vững vàng'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {teamStats.map((s, i) => (
              <div key={i} className="text-center p-8 bg-gradient-to-b from-blue-50 to-white rounded-2xl">
                <div className="text-4xl font-bold text-blue-600 mb-2">{s.value}</div>
                <div className="text-gray-700 font-medium">{lang === 'zh' ? s.label_zh : s.label_vi}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
